// メートル指定の空間操作 SQL テンプレート（純関数 / DuckDB 非依存・テスト対象）。
//
// 役割: CRS 規約（plan.md §5）の「MakeValid → 投影 → 操作 → 逆投影」の三段構えを
//       文字列生成として一箇所に固定する。エージェントのスキルが参照する正しい SQL の
//       唯一の出所であり、ここを守れば軸順序の転倒や単位ミスを構造的に防げる。
// 関係: crs.js の chooseMetricCrs() が返した投影先 CRS を受け取って組み立てる。
//       agent/skills/* と、必要なら tools 層がこのテンプレートを使う。
//
// 不変条件:
//   - EPSG:4326 を含む ST_Transform は第4引数（always_xy）に true を必ず付ける（§5.2）。
//   - 変換前に ST_MakeValid を一段かませる（§5.4）。

// CRS 文字列を SQL リテラルへ（EPSG:xxxx も PROJ 文字列も単純にクオートで囲む）。
function crsLiteral(crs) {
  return `'${String(crs).replace(/'/g, "''")}'`
}

// SQL 識別子をダブルクオートで安全に囲む。
function ident(name) {
  return `"${String(name).replace(/"/g, '""')}"`
}

export const RTREE_INDEX_MIN_FEATURES = 5000

// 取込レイヤーに R-tree index を作る価値があるかを判定する。
// 小規模レイヤーでは全走査の方が十分速く、index 作成時間とメモリが無駄になりやすい。
export function shouldCreateRTreeIndex(featureCount, threshold = RTREE_INDEX_MIN_FEATURES) {
  return Number(featureCount) >= threshold
}

// DuckDB Spatial の R-tree index 作成 SQL。
// CREATE TABLE 後、geom 列に統一されたテーブルに対して実行する。
export function buildRTreeIndexSql(table, { geomColumn = 'geom', indexName } = {}) {
  const idx = indexName || `${table}_${geomColumn}_rtree`
  return `CREATE INDEX ${ident(idx)} ON ${ident(table)} USING RTREE (${ident(geomColumn)})`
}

// メートル単位のバッファを生成する SELECT 文を組み立てる（plan.md §5.1 のテンプレート）。
//
// 引数:
//   sourceLayer: 入力テーブル名
//   radiusMeters: バッファ半径（メートル）
//   metricCrs: 投影先 CRS（chooseMetricCrs() の戻り値 crs）
//   options.geomColumn: ジオメトリ列名（既定 'geom'）
//   options.attributeColumns: 残す属性列（省略時は geom 以外を全て: "* EXCLUDE (geom)"）
//
// 戻り値: SELECT 文（末尾セミコロンなし）。呼び出し側で CREATE TABLE AS に包む。
export function buildBufferSql(sourceLayer, radiusMeters, metricCrs, options = {}) {
  const { geomColumn = 'geom', attributeColumns } = options
  const g = `"${geomColumn}"`
  const crs = crsLiteral(metricCrs)
  const attrs = attributeColumns ? attributeColumns.join(', ') : `* EXCLUDE (${g})`

  // 三段構え: MakeValid → 4326→metric 投影 → Buffer → metric→4326 逆投影。
  // ST_Transform は両方向とも第4引数 true（軸順序固定）。
  return (
    `SELECT ST_Transform(\n` +
    `         ST_Buffer(\n` +
    `           ST_Transform(ST_MakeValid(${g}), 'EPSG:4326', ${crs}, true),\n` +
    `           ${Number(radiusMeters)}),\n` +
    `         ${crs}, 'EPSG:4326', true) AS geom,\n` +
    `       ${attrs}\n` +
    `FROM "${sourceLayer}"`
  )
}

// 任意のメートル操作を三段構えで包む汎用ヘルパー。
// innerExpr は投影後ジオメトリ（metric CRS 平面）を入力に取り、metric CRS のジオメトリを
// 返す式テンプレート関数: (projectedGeomSql) => string
//
// 例（簡略化 ST_Simplify を 50m 許容で）:
//   wrapMeterOperation('roads', m => `ST_Simplify(${m}, 50)`, crs)
export function wrapMeterOperation(sourceLayer, innerExpr, metricCrs, options = {}) {
  const { geomColumn = 'geom', attributeColumns } = options
  const g = `"${geomColumn}"`
  const crs = crsLiteral(metricCrs)
  const attrs = attributeColumns ? attributeColumns.join(', ') : `* EXCLUDE (${g})`
  const projected = `ST_Transform(ST_MakeValid(${g}), 'EPSG:4326', ${crs}, true)`

  return (
    `SELECT ST_Transform(${innerExpr(projected)}, ${crs}, 'EPSG:4326', true) AS geom,\n` +
    `       ${attrs}\n` +
    `FROM "${sourceLayer}"`
  )
}
